import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
    getAllModulesRequest,
    createModuleRequest,
    updateModuleRequest,
    deleteModuleRequest,
} from '../../../redux/actions/moduleActions';
import { API_BASE_URL } from '../../../../api';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import './ModuleManagement.css';
import { ImagePlus, Pencil, Trash2, Layers, X } from 'lucide-react';

// Statuts possibles côté backend (enum ModuleStatus)
const MODULE_STATUSES = ['ACTIVE', 'INACTIVE', 'MAINTENANCE'];

const getModuleImageUrl = (filename) =>
    filename ? `${API_BASE_URL}/api/modules/images/${filename}` : null;

/* ─── Modal CRUD ───────────────────────────────────────── */
const ModuleModal = ({ isOpen, onClose, onSubmit, editingModule, loading, modules }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        status: 'ACTIVE',
        icon: '',
        picture: '',
        imageFile: null,
        removePicture: false,
        displayOrder: '',
        routePath: '',
        parentModuleId: '',
    });

    useEffect(() => {
        if (editingModule) {
            setFormData({
                code: editingModule.code || '',
                name: editingModule.name || '',
                description: editingModule.description || '',
                status: editingModule.status || 'ACTIVE',
                icon: editingModule.icon || '',
                picture: editingModule.picture || '',
                imageFile: null,
                removePicture: false,
                displayOrder: editingModule.displayOrder !== undefined && editingModule.displayOrder !== null ? String(editingModule.displayOrder) : '',
                routePath: editingModule.routePath || '',
                parentModuleId: editingModule.parentModule?.id
                    ? String(editingModule.parentModule.id)
                    : editingModule.parentModuleId
                        ? String(editingModule.parentModuleId)
                        : '',
            });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                status: 'ACTIVE',
                icon: '',
                picture: '',
                imageFile: null,
                removePicture: false,
                displayOrder: '',
                routePath: '',
                parentModuleId: '',
            });
        }
    }, [editingModule, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        setFormData((p) => ({ ...p, imageFile: file, removePicture: false }));
    };

    const clearImage = () => {
        setFormData((p) => ({ ...p, imageFile: null, removePicture: true }));
    };

    const imagePreviewUrl = useMemo(() => {
        if (formData.imageFile) return URL.createObjectURL(formData.imageFile);
        if (!formData.removePicture && formData.picture) return getModuleImageUrl(formData.picture);
        return null;
    }, [formData.imageFile, formData.picture, formData.removePicture]);

    useEffect(() => {
        return () => {
            if (imagePreviewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [imagePreviewUrl]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.code.trim()) {
            Swal.fire({ icon: 'warning', text: t('Le code du module est obligatoire') });
            return;
        }
        if (!formData.name.trim()) {
            Swal.fire({ icon: 'warning', text: t('Le nom du module est obligatoire') });
            return;
        }
        onSubmit({
            code: formData.code.trim(),
            name: formData.name.trim(),
            description: formData.description.trim() || '',
            status: formData.status,
            icon: formData.icon.trim() || '',
            removePicture: !!formData.removePicture,
            imageFile: formData.imageFile || null,
            displayOrder: formData.displayOrder !== '' ? Number(formData.displayOrder) : null,
            routePath: formData.routePath.trim() || '',
            parentModuleId: formData.parentModuleId ? Number(formData.parentModuleId) : null,
        });
    };

    // Exclure le module en cours d'édition des parents possibles
    const parentOptions = (modules || []).filter(
        (m) => !editingModule || m.id !== editingModule.id
    );

    if (!isOpen) return null;

    return (
        <div className="mm-overlay" onClick={onClose}>
            <div className="mm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mm-modal-header">
                    <h3>{editingModule ? t('Modifier le module') : t('Ajouter un module')}</h3>
                    <button className="mm-modal-close" onClick={onClose} type="button">✕</button>
                </div>

                <form className="mm-form" onSubmit={handleSubmit}>
                    <div className="mm-section-title">{t('Identité')}</div>
                    <div className="mm-grid">
                        <div className="mm-field">
                            <label>{t('Code')} *</label>
                            <input name="code" value={formData.code} onChange={handleChange} required maxLength={50} placeholder="Ex : MOD-RH" />
                        </div>
                        <div className="mm-field">
                            <label>{t('Nom du module')} *</label>
                            <input name="name" value={formData.name} onChange={handleChange} required maxLength={200} placeholder="Ex : Gestion RH" />
                        </div>
                        <div className="mm-field mm-full">
                            <label>{t('Description')}</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} rows={2} placeholder={t('Description du module...')} />
                        </div>
                    </div>

                    <div className="mm-section-title" style={{ marginTop: 14 }}>{t('Configuration')}</div>
                    <div className="mm-grid">
                        <div className="mm-field">
                            <label>{t('Statut')}</label>
                            <select name="status" value={formData.status} onChange={handleChange}>
                                {MODULE_STATUSES.map((s) => (
                                    <option key={s} value={s}>{t(s)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mm-field">
                            <label>{t('Ordre d\'affichage')}</label>
                            <input name="displayOrder" type="number" value={formData.displayOrder} onChange={handleChange} min={0} placeholder="Ex : 1" />
                        </div>
                        <div className="mm-field">
                            <label>{t('Icône')}</label>
                            <input name="icon" value={formData.icon} onChange={handleChange} placeholder="Ex : dashboard" />
                        </div>
                        <div className="mm-field mm-full">
                            <label>{t('Image du module')}</label>
                            <div className="mm-picture-card">
                                <div className="mm-picture-preview">
                                    {imagePreviewUrl ? (
                                        <img src={imagePreviewUrl} alt={formData.name || t('Module')} />
                                    ) : (
                                        <div className="mm-picture-empty">
                                            <ImagePlus size={22} />
                                            <span>{t('Aucune image')}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mm-picture-actions">
                                    <label className="mm-btn mm-btn-outline">
                                        <ImagePlus size={15} />
                                        {t('Choisir une image')}
                                        <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                                    </label>
                                    {(formData.picture || formData.imageFile) && (
                                        <button type="button" className="mm-btn mm-btn-outline" onClick={clearImage}>
                                            <X size={15} />
                                            {t('Retirer')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mm-field">
                            <label>{t('Route')}</label>
                            <input name="routePath" value={formData.routePath} onChange={handleChange} placeholder="Ex : /rh/dashboard" />
                        </div>
                        <div className="mm-field mm-full">
                            <label>{t('Module parent')}</label>
                            <select name="parentModuleId" value={formData.parentModuleId} onChange={handleChange}>
                                <option value="">{t('— Aucun (module racine) —')}</option>
                                {parentOptions.map((m) => (
                                    <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mm-modal-actions">
                        <button type="button" className="mm-btn mm-btn-outline" onClick={onClose} disabled={loading}>{t('Annuler')}</button>
                        <button type="submit" className="mm-btn mm-btn-primary" disabled={loading}>
                            {loading ? t('Chargement...') : editingModule ? t('Enregistrer') : t('Créer le module')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ─── Helpers ─────────────────────────────────────────── */
const statusClass = (status) => {
    if (status === 'ACTIVE') return 'on';
    if (status === 'MAINTENANCE') return 'warn';
    return 'off';
};

const statusLabel = (status, t) => {
    if (status === 'ACTIVE') return t('Actif');
    if (status === 'MAINTENANCE') return t('Maintenance');
    return t('Inactif');
};

/* ─── Main ─────────────────────────────────────────────── */
const ModuleManagement = ({
                              modules,
                              loading,
                              message,
                              error,
                              getAllModules,
                              createModule,
                              updateModule,
                              deleteModule,
                          }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => { getAllModules(); }, [getAllModules]);

    const openCreate = () => { setEditingModule(null); setIsModalOpen(true); };
    const openEdit = (m) => { setEditingModule(m); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditingModule(null); };

    const handleSubmit = (formData) => {
        if (editingModule) {
            updateModule(editingModule.id, formData);
        } else {
            createModule(formData);
        }
        closeModal();
    };

    const handleDelete = (id, name) => {
        Swal.fire({
            title: t('Supprimer'),
            text: `${t('Supprimer le module')} "${name}" ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('Oui, supprimer'),
            cancelButtonText: t('Annuler'),
        }).then((res) => { if (res.isConfirmed) deleteModule(id); });
    };

    const filtered = useMemo(() => {
        return (modules || []).filter((m) => {
            const q = searchTerm.toLowerCase().trim();
            const matchSearch = !q ||
                (m.code || '').toLowerCase().includes(q) ||
                (m.name || '').toLowerCase().includes(q) ||
                (m.routePath || '').toLowerCase().includes(q);
            const matchStatus = statusFilter === 'all' ? true : m.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [modules, searchTerm, statusFilter]);

    return (
        <div className="mm-page">
            {/* Header */}
            <div className="mm-header">
                <div>
                    <h2>
                        <Layers size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        {t('Gestion des modules')}
                    </h2>
                    <p className="mm-subtitle">{t('Gérez les modules et leur hiérarchie')}</p>
                </div>
                <div className="mm-header-actions">
                    <button className="mm-btn mm-btn-primary" onClick={openCreate} type="button">
                        + {t('Ajouter un module')}
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="mm-toolbar">
                <input
                    className="mm-search"
                    placeholder={t('Rechercher (code, nom, route...)')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="mm-filter-tabs">
                    {[
                        { key: 'all', label: t('Tous') },
                        { key: 'ACTIVE', label: t('Actifs') },
                        { key: 'INACTIVE', label: t('Inactifs') },
                        { key: 'MAINTENANCE', label: t('Maintenance') },
                    ].map((f) => (
                        <button
                            key={f.key}
                            className={`mm-filter-tab ${statusFilter === f.key ? 'active' : ''}`}
                            onClick={() => setStatusFilter(f.key)}
                            type="button"
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="mm-meta">
                    {loading ? t('Chargement...') : `${filtered.length} module${filtered.length !== 1 ? 's' : ''}`}
                </div>
            </div>

            {message && <div className="mm-alert mm-alert-success">{message}</div>}
            {error && <div className="mm-alert mm-alert-danger">{error}</div>}

            {/* Table */}
            <div className="mm-table-wrapper">
                {loading ? (
                    <div className="mm-loading"><div className="mm-spinner" /><span>{t('Chargement...')}</span></div>
                ) : filtered.length === 0 ? (
                    <div className="mm-empty"><p>{t('Aucun module trouvé')}</p></div>
                ) : (
                    <table className="mm-table">
                        <thead>
                        <tr>
                            <th>{t('Code')}</th>
                            <th>{t('Nom')}</th>
                            <th>{t('Route')}</th>
                            <th>{t('Ordre')}</th>
                            <th>{t('Parent')}</th>
                            <th>{t('Statut')}</th>
                            <th style={{ width: 100, textAlign: 'right' }}>{t('Actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.map((m) => (
                            <tr key={m.id}>
                                <td>
                                    <div className="mm-code-cell">
                                        {m.picture ? (
                                            <img className="mm-module-thumb" src={getModuleImageUrl(m.picture)} alt={m.name} />
                                        ) : (
                                            m.icon && <span className="mm-icon-badge">{m.icon.charAt(0).toUpperCase()}</span>
                                        )}
                                        <span className="mm-code">{m.code}</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="mm-name">{m.name}</div>
                                    {m.description && <div className="mm-desc">{m.description}</div>}
                                </td>
                                <td>
                                    {m.routePath
                                        ? <span className="mm-route">{m.routePath}</span>
                                        : <span className="mm-muted">—</span>}
                                </td>
                                <td>
                                    {m.displayOrder !== null && m.displayOrder !== undefined
                                        ? <span className="mm-order">{m.displayOrder}</span>
                                        : <span className="mm-muted">—</span>}
                                </td>
                                <td>
                                    {m.parentModuleName
                                        ? <span className="mm-parent-badge">{m.parentModuleName}</span>
                                        : <span className="mm-root-badge">{t('Racine')}</span>}
                                </td>
                                <td>
                                    <button type="button" className={`mm-toggle ${statusClass(m.status)}`}>
                                        <span className="mm-toggle-knob" />
                                        <span>{statusLabel(m.status, t)}</span>
                                    </button>
                                </td>
                                <td className="mm-row-actions">
                                    <button className="mm-link-btn" onClick={() => openEdit(m)} title={t('Modifier')} type="button">
                                        <Pencil size={15} />
                                    </button>
                                    <button className="mm-link-btn danger" onClick={() => handleDelete(m.id, m.name)} title={t('Supprimer')} type="button">
                                        <Trash2 size={15} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <ModuleModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                editingModule={editingModule}
                loading={loading}
                modules={modules}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    modules: state.module.modules || [],
    loading: state.module.loading,
    message: state.module.message,
    error: state.module.error,
});

const mapDispatchToProps = (dispatch) => ({
    getAllModules: () => dispatch(getAllModulesRequest()),
    createModule: (data) => dispatch(createModuleRequest(data)),
    updateModule: (id, data) => dispatch(updateModuleRequest(id, data)),
    deleteModule: (id) => dispatch(deleteModuleRequest(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModuleManagement);
